import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { UserMinus, Search, Download, RefreshCw, ExternalLink, FileText, Users2, CalendarDays, Timer } from "lucide-react";
import { formatDate } from "@/lib/dates";

const REASON_LABEL = {
  resigned: "Resigned",
  terminated: "Terminated",
  redundancy: "Redundancy",
  end_of_contract: "End of contract",
  retired: "Retired",
  other: "Other",
};

const STATUS_PILL = {
  draft: "pill-warning",
  confirmed: "pill-danger",
  reversed: "pill-info",
};

export default function LeaversPage() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (reason) params.reason = reason;
      const [{ data: list }, { data: s }] = await Promise.all([
        api.get("/leavers", { params }),
        api.get("/leavers/stats/summary"),
      ]);
      setRows(list || []);
      setStats(s);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load leavers");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [status, reason]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => rows.filter((r) => {
    if (!q.trim()) return true;
    const hay = `${r.employee_name || ""} ${r.work_email || ""} ${r.job_title || ""} ${r.department || ""}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  }), [rows, q]);

  const exportCsv = async () => {
    setBusy(true);
    try {
      const qs = new URLSearchParams();
      if (status) qs.set("status", status);
      if (reason) qs.set("reason", reason);
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/leavers/export/csv?${qs.toString()}`;
      const token = localStorage.getItem("cc_token");
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "leavers.csv";
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error("Export failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fade-up" data-testid="leavers-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="label-eyebrow mb-2">People</div>
          <h1 className="font-display text-4xl font-bold flex items-center gap-3">
            <UserMinus className="w-8 h-8" /> Leavers Register
          </h1>
          <p className="text-[var(--cc-text-soft)] mt-1 text-sm max-w-2xl">
            Everyone who has left, with their reason, notice and last working day. Leavers are hidden
            from the Pipeline, CQC Compliance, DBS, References, Right to Work, Onboarding and
            Employees views — their full history stays here for audit and CQC inspection.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button className="btn-ghost inline-flex items-center gap-1.5" onClick={load} disabled={busy} data-testid="leavers-refresh-button">
            <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button className="btn-primary inline-flex items-center gap-1.5" onClick={exportCsv} disabled={busy} data-testid="leavers-export-button">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="leavers-kpis">
          <Kpi title="Confirmed leavers" value={stats.confirmed_leavers} icon={UserMinus} accent />
          <Kpi title="Drafts in progress" value={stats.drafts} icon={FileText} />
          <Kpi title="Active headcount" value={stats.active_headcount} icon={Users2} />
          <Kpi
            title="Avg tenure"
            value={stats.avg_tenure_days != null ? `${(stats.avg_tenure_days / 30.44).toFixed(1)} mo` : "—"}
            icon={Timer}
          />
        </div>
      )}

      {stats && Object.keys(stats.by_reason || {}).length > 0 && (
        <div className="cc-card p-4 mb-4" data-testid="leavers-by-reason">
          <div className="label-eyebrow mb-2 inline-flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" /> Reasons for leaving</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(stats.by_reason).map(([k, v]) => (
              <span key={k} className="pill pill-info !text-[11px]" data-testid={`leavers-reason-chip-${k}`}>
                {REASON_LABEL[k] || k}: {v}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="cc-card p-4 mb-4 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="leavers-filters">
        <div className="flex items-center gap-2 md:col-span-1">
          <Search className="w-4 h-4 opacity-50" />
          <input
            className="cc-input border-none shadow-none flex-1"
            placeholder="Search name, email, job title…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="leavers-search-input"
          />
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Record status</label>
          <select className="cc-select" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="leavers-status-filter">
            <option value="">All</option>
            <option value="confirmed">Confirmed</option>
            <option value="draft">Draft</option>
            <option value="reversed">Reversed</option>
          </select>
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Reason</label>
          <select className="cc-select" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="leavers-reason-filter">
            <option value="">All</option>
            {Object.entries(REASON_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
      </div>

      <div className="cc-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--cc-surface)]">
            <tr className="text-left">
              <th className="px-4 py-3 label-eyebrow">Employee</th>
              <th className="px-4 py-3 label-eyebrow">Role</th>
              <th className="px-4 py-3 label-eyebrow">Reason</th>
              <th className="px-4 py-3 label-eyebrow">Notice given</th>
              <th className="px-4 py-3 label-eyebrow">Last working day</th>
              <th className="px-4 py-3 label-eyebrow">Tenure</th>
              <th className="px-4 py-3 label-eyebrow">Rehire</th>
              <th className="px-4 py-3 label-eyebrow">Record</th>
              <th className="px-4 py-3 label-eyebrow text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {busy && rows.length === 0 && (
              <tr><td colSpan="9" className="text-center py-10 text-[var(--cc-text-soft)]">Loading…</td></tr>
            )}
            {!busy && filtered.length === 0 && (
              <tr><td colSpan="9" className="text-center py-10 text-[var(--cc-text-soft)]" data-testid="leavers-empty">
                {rows.length === 0 ? "No leavers recorded — mark a leaver from any employee profile." : "No leavers match your filters."}
              </td></tr>
            )}
            {filtered.map((r) => (
              <tr key={r.id} className="border-t border-[var(--cc-border)] hover:bg-[var(--cc-surface)]" data-testid={`leaver-row-${r.id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium">{r.employee_name || "—"}</div>
                  {r.work_email && <div className="text-[11px] text-[var(--cc-text-soft)]">{r.work_email}</div>}
                </td>
                <td className="px-4 py-3 text-xs">
                  {r.job_title || "—"}
                  {r.department && <div className="text-[11px] text-[var(--cc-text-soft)]">{r.department}</div>}
                </td>
                <td className="px-4 py-3 text-xs">
                  {REASON_LABEL[r.reason] || r.reason || "—"}
                  {r.reason_notes && <div className="text-[11px] text-[var(--cc-text-soft)] max-w-xs truncate" title={r.reason_notes}>{r.reason_notes}</div>}
                </td>
                <td className="px-4 py-3 text-xs font-mono">{r.resignation_date ? formatDate(r.resignation_date) : "—"}</td>
                <td className="px-4 py-3 text-xs font-mono">{r.last_working_day ? formatDate(r.last_working_day) : "—"}</td>
                <td className="px-4 py-3 text-xs">
                  {r.tenure_days != null ? `${(r.tenure_days / 30.44).toFixed(1)} mo` : "—"}
                </td>
                <td className="px-4 py-3 text-xs">
                  {r.rehire_eligible === true ? <span className="pill pill-success !text-[10px]">Eligible</span>
                    : r.rehire_eligible === false ? <span className="pill pill-danger !text-[10px]">Not eligible</span>
                    : "—"}
                </td>
                <td className="px-4 py-3">
                  <span className={`pill !text-[10px] ${STATUS_PILL[r.status] || ""}`}>{r.status}</span>
                </td>
                <td className="px-4 py-3 text-right">
                  <Link
                    to={`/app/employees/${r.employee_id}`}
                    className="btn-ghost text-xs inline-flex items-center gap-1"
                    data-testid={`leaver-open-profile-${r.id}`}
                  >
                    <ExternalLink className="w-3 h-3" /> Open record
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ title, value, icon: Icon, accent }) {
  return (
    <div className="cc-card p-5" data-testid={`leavers-kpi-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="label-eyebrow mb-1 inline-flex items-center gap-1.5">
        {Icon && <Icon className="w-3.5 h-3.5" />} {title}
      </div>
      <div className={`font-display text-3xl font-bold ${accent ? "text-[var(--cc-accent)]" : ""}`}>{value ?? "—"}</div>
    </div>
  );
}
